'use client'

import getUserName from '@/helpers/get-user-name'
import { getUserProfilePictureUrl } from '@/helpers/user'
import { hasAllPermissions, hasAnyPermission } from '@/lib/auth/permissions'
import { ArrowRight, Settings, X } from 'lucide-react'
import { signOut } from 'next-auth/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import ChevronDown from '../icons/chevron-down'
import HelmetIcon from '../icons/helmet-icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet'
import ImageWithFallback from './image-with-fallback'
import Loader from './loader'

export default function ProfileDropdown({
  customTrigger,
  session = null,
  compact = false,
}) {
  const pathname = usePathname()
  const [loggingOut, setLoggingOut] = useState(false)
  const [open, setOpen] = useState(false)

  function logout() {
    setLoggingOut(true)
    signOut({ callbackUrl: pathname })
  }

  if (loggingOut) {
    return (
      <Loader size="overlay" logo="animated" content="Logging you out..." />
    )
  }

  if (!session) {
    return null
  }

  const user = session.user
  const canViewAdminTools = hasAnyPermission(user, ['site_admin', 'developer'])
  const canViewDevTools = hasAllPermissions(user, 'developer')

  if (customTrigger) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            aria-label="Open account menu"
            className="outline-hidden"
          >
            {customTrigger}
          </button>
        </SheetTrigger>
        <SheetContent
          side="right"
          showClose={false}
          className="flex h-dvh w-[min(86vw,22rem)] max-w-none flex-col gap-0 rounded-none border-neutral-blue-700 bg-neutral-blue-900 p-0 text-shades-0"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Account</SheetTitle>
          </SheetHeader>
          <div className="flex h-full flex-col pt-[max(16px,env(safe-area-inset-top))]">
            <div className="flex items-center justify-between border-b border-neutral-blue-700 px-5 pb-4">
              <p className="text-h-6 font-semibold text-shades-0">Account</p>
              <SheetClose asChild>
                <button
                  type="button"
                  aria-label="Close account menu"
                  className="grid h-11 w-11 place-items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 transition-all hover:border-neutral-blue-500"
                >
                  <X className="size-5 stroke-shades-0" />
                </button>
              </SheetClose>
            </div>

            <div className="flex items-center gap-3 border-b border-neutral-blue-700 px-5 py-4">
              <div className="relative h-10 w-10 overflow-hidden rounded-full">
                <ImageWithFallback
                  id={user.id}
                  src={getUserProfilePictureUrl(user)}
                  alt={user.username}
                  fill
                  sizes="40px"
                  className="object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="truncate text-p-2 font-semibold text-shades-0">
                  {getUserName(user)}
                </p>
                <p className="truncate text-cap text-neutral-blue-300">
                  {user.username}
                </p>
              </div>
            </div>

            <nav className="flex flex-1 flex-col px-4 py-4">
              <SheetClose asChild>
                <Link
                  href="/profile-manager/driver/calendar"
                  className="mb-1 flex min-h-12 items-center gap-3 rounded-sm border border-transparent px-4 py-3 text-p-2 font-semibold text-neutral-blue-100 transition-all hover:border-neutral-blue-700 hover:bg-neutral-blue-800"
                >
                  <HelmetIcon className="size-5 stroke-neutral-blue-100" />
                  Driver Dashboard
                </Link>
              </SheetClose>

              <div className="mt-2 h-px w-full bg-neutral-blue-700" />

              <SheetClose asChild>
                <Link
                  href="/account"
                  className="mb-1 flex min-h-12 items-center gap-3 rounded-sm border border-transparent px-4 py-3 text-p-2 font-semibold text-neutral-blue-100 transition-all hover:border-neutral-blue-700 hover:bg-neutral-blue-800"
                >
                  <Settings className="size-5" />
                  Settings
                </Link>
              </SheetClose>

              {canViewAdminTools && (
                <SheetClose asChild>
                  <Link
                    href="/admin-tools"
                    className="mb-1 flex min-h-12 items-center gap-3 rounded-sm border border-transparent px-4 py-3 text-p-2 font-semibold text-warning transition-all hover:border-neutral-blue-700 hover:bg-neutral-blue-800"
                  >
                    <Settings className="size-5" />
                    Admin Tools
                  </Link>
                </SheetClose>
              )}

              {canViewDevTools && (
                <SheetClose asChild>
                  <Link
                    href="/dev-tools"
                    className="mb-1 flex min-h-12 items-center gap-3 rounded-sm border border-transparent px-4 py-3 text-p-2 font-semibold text-primary transition-all hover:border-neutral-blue-700 hover:bg-neutral-blue-800"
                  >
                    <Settings className="size-5" />
                    Dev Tools
                  </Link>
                </SheetClose>
              )}

              <button
                type="button"
                onClick={logout}
                className="mt-4 flex min-h-12 items-center gap-3 rounded-sm border border-neutral-blue-600 bg-neutral-blue-800 px-4 py-3 text-left text-p-2 font-semibold text-shades-0 transition-all hover:border-shades-0 hover:bg-shades-0 hover:text-neutral-blue-900"
              >
                <ArrowRight className="size-5" />
                Logout
              </button>
            </nav>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger aria-label="Account menu" className="outline-hidden">
        <div className="flex items-center gap-3 hover:cursor-pointer">
          <div className="relative h-8 w-8 hover:cursor-pointer">
            <div className="relative h-8 w-8 overflow-hidden rounded-full">
              <ImageWithFallback
                id={user.id}
                src={getUserProfilePictureUrl(user)}
                alt={user.username}
                fill
                sizes="32px"
                className="object-cover"
              />
            </div>
          </div>
          {!compact ? (
            <p className="font-normal text-shades-0">{getUserName(user)}</p>
          ) : null}
          <ChevronDown className="h-3 w-3 stroke-white stroke-[3px]" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56 border-neutral-blue-600 bg-neutral-blue-900 px-5 py-3 font-inter text-shades-0"
        align="end"
      >
        <DropdownMenuLabel className="text-cap font-bold text-neutral-blue-400">
          My Account
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-neutral-blue-700" />
        <DropdownMenuItem asChild>
          <Link
            href="/account"
            className="flex cursor-pointer items-center gap-2 text-p-1 font-semibold focus:bg-neutral-blue-800 focus:text-shades-0"
          >
            <Settings className="size-5" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-neutral-blue-700" />
        <DropdownMenuItem
          onClick={logout}
          className="flex cursor-pointer items-center gap-2 text-p-1 font-semibold focus:bg-neutral-blue-800 focus:text-shades-0"
        >
          <ArrowRight className="size-5" />
          <span>Logout</span>
        </DropdownMenuItem>

        {canViewAdminTools && (
          <>
            <DropdownMenuSeparator className="bg-neutral-blue-700" />
            <DropdownMenuItem asChild>
              <Link
                href="/admin-tools"
                className="flex cursor-pointer items-center gap-2 text-p-1 font-bold text-warning focus:bg-neutral-blue-800 focus:text-warning"
              >
                <Settings className="size-5" />
                <span>Admin Tools</span>
              </Link>
            </DropdownMenuItem>
          </>
        )}

        {canViewDevTools && (
          <DropdownMenuItem asChild>
            <Link
              href="/dev-tools"
              className="flex cursor-pointer items-center gap-2 text-p-1 font-bold text-primary focus:bg-neutral-blue-800 focus:text-primary"
            >
              <Settings className="size-5" />
              <span>Dev Tools</span>
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
